import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/apiConstants';
import { getStoredTokens } from '../../utils/auth';

const AnalysisActions = ({ analysisData }) => {
    const navigate = useNavigate();
    const [isApproving, setIsApproving] = useState(false);
    const [error, setError] = useState(null);
    const tokens = getStoredTokens();

    const handleApprove = async () => {
        try {
            setIsApproving(true);
            setError(null);
            console.log('Starting approval process for analysis:', analysisData);

            // Check if authentication token is available
            if (!tokens || !tokens.idToken) {
                throw new Error('Authentication required. Please log in and try again.');
            }

            // Try to get analysis ID from multiple possible locations
            let analysisId = analysisData?.id || analysisData?._id;

            // If still no ID, log the actual structure for debugging
            if (!analysisId) {
                console.error('Analysis ID not found. Available keys:', Object.keys(analysisData || {}));
                console.error('Full analysisData:', JSON.stringify(analysisData, null, 2));
                throw new Error('Analysis ID is missing. Please try scanning again.');
            }

            console.log('Approving analysis:', analysisId);

            // Send POST request to approve the analysis
            const response = await fetch(
                `${API_BASE_URL}${API_ENDPOINTS.WEBPAGE_ANALYZER_APPROVE}/${analysisId}/approve`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${tokens.idToken}`
                    },
                }
            );

            if (!response.ok) {
                let errorMessage = 'Failed to approve analysis';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorMessage;
                } catch (parseError) {
                    // If JSON parsing fails, try to get plain text response
                    try {
                        const textError = await response.text();
                        errorMessage = textError || errorMessage;
                    } catch {
                        // If that also fails, use the status code
                        errorMessage = `Server error (${response.status})`;
                    }
                }
                throw new Error(errorMessage);
            }

            const result = await response.json();
            console.log('Analysis approved successfully:', result);

            // Navigate to chatbot with the approved website data
            navigate('/adflyn-chatbot', {
                state: {
                    websiteData: analysisData,
                    approved: true,
                    analysisId: analysisId
                }
            });
        } catch (err) {
            console.error('Error approving analysis:', err);
            setError(err.message || 'Failed to approve the analysis. Please try again.');
            setIsApproving(false);
        }
    };

    return (
        <div className="analysis-actions">
            {error && (
                <div className="error-message" style={{
                    color: '#dc2626',
                    marginBottom: '12px',
                    padding: '8px',
                    backgroundColor: '#fee2e2',
                    borderRadius: '4px'
                }}>
                    {error}
                </div>
            )}
            <button
                className="approve-btn"
                onClick={handleApprove}
                disabled={isApproving}
                style={{
                    opacity: isApproving ? 0.6 : 1,
                    cursor: isApproving ? 'not-allowed' : 'pointer'
                }}
            >
                {isApproving ? 'Approving...' : 'Approve & Continue to Chatbot'}
            </button>
        </div>
    );
};

AnalysisActions.propTypes = {
    analysisData: PropTypes.object.isRequired
};

export default AnalysisActions;
